import {
  AppCard,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiDownload, mdiEye } from "@mdi/js";
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useShowServicePurchaseOrderDetails } from "../../../../../__generated__/orval/endpoints/purchase-orders/purchase-orders";
import { EntitiesPurchaseOrderServiceQuotationMemberMain } from "../../../../../__generated__/orval/models/entitiesPurchaseOrderServiceQuotationMemberMain.zod";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import { i18nextParseToDate } from "../../../../../utils/i18nextParseTo";

const ServicePurchaseOrderDetails: React.FC = () => {
  const { purchaseOrderId } = Route.useParams();
  const { t } = useTranslation();
  const purchaseOrderIdNumber = Number(purchaseOrderId);
  const {
    data: purchaseOrder,
    isError,
    isLoading,
  } = useShowServicePurchaseOrderDetails(purchaseOrderIdNumber, {
    query: {
      enabled:
        Number.isInteger(purchaseOrderIdNumber) && purchaseOrderIdNumber > 0,
      select: (response) => {
        const result =
          EntitiesPurchaseOrderServiceQuotationMemberMain.safeParse(response);

        if (!result.success) {
          throw new Error(result.error.message);
        }

        return result.data;
      },
    },
  });

  return (
    <PageContainer
      maxWidth={false}
      showBackButton
      title={t("servicePurchaseOrders.detailsTitle")}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress aria-label={t("common.loading")} />
        </Box>
      ) : isError || !purchaseOrder ? (
        <Typography color="error" role="alert">
          {t("servicePurchaseOrders.detailsFetchError")}
        </Typography>
      ) : (
        <AppCard>
          {purchaseOrder.service_quotations.length > 0 ? (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table
                aria-label={t("servicePurchaseOrders.detailsTitle")}
                size="small"
                sx={{ minWidth: 1150 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("servicePurchaseOrders.purchaseOrderNumber")}
                    </TableCell>
                    <TableCell>
                      {t("servicePurchaseOrders.requisitionNumber")}
                    </TableCell>
                    <TableCell>{t("servicePurchaseOrders.status")}</TableCell>
                    <TableCell>
                      {t("servicePurchaseOrders.criticality")}
                    </TableCell>
                    <TableCell>{t("servicePurchaseOrders.plant")}</TableCell>
                    <TableCell>{t("common.vendor")}</TableCell>
                    <TableCell>{t("common.date")}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {t("common.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrder.service_quotations.map((quotation) => (
                    <TableRow hover key={quotation.id}>
                      <TableCell>{`#${purchaseOrder.po_number}`}</TableCell>
                      <TableCell>{quotation.pr_item.pr_number}</TableCell>
                      <TableCell>{quotation.status}</TableCell>
                      <TableCell>
                        {quotation.pr_item.criticality || "-"}
                      </TableCell>
                      <TableCell>
                        {quotation.pr_item.plant_name || "-"}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {purchaseOrder.vendor_name}
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {quotation.vendor_email || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {t(
                          "intlDate",
                          i18nextParseToDate(
                            purchaseOrder.sap_created_at ||
                              purchaseOrder.created_at,
                          ),
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <Box
                          sx={{
                            alignItems: "center",
                            display: "flex",
                            flexWrap: "nowrap",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          {purchaseOrder.purchase_order_pdf_filename ? (
                            <Tooltip title={t("common.download")}>
                              <IconButton
                                aria-label={t("common.download")}
                                component="a"
                                download={
                                  purchaseOrder.purchase_order_pdf_filename
                                }
                                href={`/vbuyer/purchase_orders/${purchaseOrder.id}/download.pdf`}
                                size="small"
                                target="_blank"
                              >
                                <Icon icon={mdiDownload} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t("attachments.noAttachments")}>
                              <span>
                                <IconButton
                                  aria-label={t("common.download")}
                                  disabled
                                  size="small"
                                >
                                  <Icon icon={mdiDownload} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={t("servicePurchaseOrders.moreDetails")}
                          >
                            <IconButton
                              aria-label={t(
                                "servicePurchaseOrders.moreDetails",
                              )}
                              component="a"
                              href={`/vbuyer/service_quotations/${quotation.id}`}
                              size="small"
                            >
                              <Icon icon={mdiEye} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" sx={{ py: 3 }}>
              {t("servicePurchaseOrders.noQuotations")}
            </Typography>
          )}
        </AppCard>
      )}
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/purchase_orders/service_quotations/$purchaseOrderId/",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Purchase Order - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: ServicePurchaseOrderDetails,
});
